import { Component, OnInit } from '@angular/core';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { PerformanceMenuService } from '@app/modules/performance/services';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { ColorService } from '@app/services/color.service';
import { FeatureService } from '@app/services/feature.service';
import { Goal } from '@models/performance/goal.model';
import { Review } from '@models/performance/review.model';
import { Status } from '@performance/enums/status.enum';
import { ArcElement, Chart, DoughnutController, Tooltip } from 'chart.js';

@Component({
    templateUrl: './overview-show.template.html',
    styleUrls: ['./overview-show.style.scss'],
})
export class OverviewShowView implements OnInit {
    isLoading = false;

    isReviewsLoading = true;
    isGoalsLoading = true;
    hasGoals = false;
    hasReviews = false;

    createReviewPermission = Review.permission.edit;
    createGoalPermission = Goal.permission.edit;

    Status = Status;

    isPerformanceImprovementsFeatureFlagEnabled: boolean = false;

    reviewStats: { [key: string]: number } = {
        inProgress: 0,
        scheduled: 0,
        overdue: 0,
        completed: 0,
    };

    goalColors: { [key: string]: string } = {
        completed: this.colorService.getColorValue('success', 400),
        inProgress: this.colorService.getColorValue('warning', 500),
        overdue: this.colorService.getColorValue('error', 700),
        scheduled: this.colorService.getColorValue('primary', 500),
    };

    goalStats: { [key: string]: number } = {
        completed: 0,
        inProgress: 0,
        scheduled: 0,
        overdue: 0,
    };

    constructor(
        private auth: AuthService,
        private analyticService: AnalyticService,
        private featureService: FeatureService,
        private colorService: ColorService,
        public performanceMenuService: PerformanceMenuService
    ) {
        Chart.register(DoughnutController, ArcElement, Tooltip);
        this.loadReviews();
        this.loadGoals();
    }

    async ngOnInit(): Promise<void> {
        this.isPerformanceImprovementsFeatureFlagEnabled = await this.featureService.has(
            FeatureFlag.performanceReviewImprovements
        );
    }

    trackCreatingGoal(): void {
        this.analyticService.trackEvent(AnalyticEvents.PerformanceOverviewClickCreateGoal);
    }

    trackCreatingReview(): void {
        this.analyticService.trackEvent(AnalyticEvents.PerformanceOverviewClickCreateReview);
    }

    private async loadReviews(): Promise<void> {
        const reviewsInProgress = Review.param('company', this.auth.company.id)
            .where('statusId', [Status.InProgress])
            .limit(1)
            .get();
        const reviewsCompleted = Review.param('company', this.auth.company.id)
            .where('statusId', [Status.Completed])
            .limit(1)
            .get();
        const reviewsOverdue = Review.param('company', this.auth.company.id)
            .where('statusId', [Status.Overdue])
            .limit(1)
            .get();
        const reviewsScheduled = Review.param('company', this.auth.company.id)
            .where('statusId', [Status.Scheduled])
            .limit(1)
            .get();

        const responses = await Promise.all([reviewsInProgress, reviewsCompleted, reviewsOverdue, reviewsScheduled]);

        this.reviewStats.inProgress = responses[0][1].pagination.total;
        this.reviewStats.completed = responses[1][1].pagination.total;
        this.reviewStats.overdue = responses[2][1].pagination.total;
        this.reviewStats.scheduled = responses[3][1].pagination.total;

        this.hasReviews = Boolean(
            this.reviewStats.inProgress ||
                this.reviewStats.completed ||
                this.reviewStats.overdue ||
                this.reviewStats.scheduled
        );

        this.isReviewsLoading = false;
    }

    private async loadGoals(): Promise<void> {
        const goalsInProgress = Goal.param('company', this.auth.company.id)
            .where('statusId', [Status.InProgress])
            .limit(1)
            .get();
        const goalsCompleted = Goal.param('company', this.auth.company.id)
            .where('statusId', [Status.Completed])
            .limit(1)
            .get();
        const goalsOverdue = Goal.param('company', this.auth.company.id)
            .where('statusId', [Status.Overdue])
            .limit(1)
            .get();
        const goalsScheduled = Goal.param('company', this.auth.company.id)
            .where('statusId', [Status.Scheduled])
            .limit(1)
            .get();

        const responses = await Promise.all([goalsInProgress, goalsCompleted, goalsOverdue, goalsScheduled]);

        this.goalStats.inProgress = responses[0][1].pagination.total;
        this.goalStats.completed = responses[1][1].pagination.total;
        this.goalStats.overdue = responses[2][1].pagination.total;
        this.goalStats.scheduled = responses[3][1].pagination.total;

        this.hasGoals = Boolean(
            this.goalStats.inProgress || this.goalStats.completed || this.goalStats.overdue || this.goalStats.scheduled
        );

        this.isGoalsLoading = false;

        if (this.hasGoals) {
            setTimeout(() => {
                this.renderGoalChart();
            });
        }
    }

    private renderGoalChart(): void {
        const ctx = $('#goalChart')[0]?.getContext('2d');

        // The goal chart may no longer be visible
        // This happens when the user navigates quickly away from the current page
        if (!ctx) {
            return;
        }

        new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: ['Complete', 'In progress', 'Overdue', 'scheduled'],
                datasets: [
                    {
                        label: 'Status',
                        backgroundColor: Object.values(this.goalColors),
                        data: [
                            this.goalStats.completed,
                            this.goalStats.inProgress,
                            this.goalStats.overdue,
                            this.goalStats.scheduled,
                        ],
                    },
                ],
            },
            options: {
                borderColor: this.colorService.getCSSVariable('--card-background-color'),
                cutout: '65%',
                aspectRatio: 1.6,
                plugins: {
                    legend: { display: false },
                    datalabels: { display: false },
                },
            },
        });
    }
}
